import {BitmapText, Graphics, IBitmapTextStyle, Rectangle} from "pixi.js";
import {Subscription} from "nate-react-api-helpers";
import {FrmShape} from "./FrmShape";
import {distance, EPS, FrmShapePoint} from "./FrmShapePoint";
import {FrmVar, FrmVarEvent} from "./FrmVar";

// which side of the drawing's bounding box the dimension is rendered on.
// vertical dimensions live on "left"/"right", horizontal dimensions on "top"/"bottom".
export type DimensionSide = "left" | "right" | "top" | "bottom";

// axis-aligned bounding box of the drawing geometry, in shape units
export type BBox = {minX: number, minY: number, maxX: number, maxY: number};

// dimension rendering (all in shape units)
// gap between the drawing's bounding box and the first column of dimensions
const DIM_BASE_GAP = 14;
// distance between stacked dimension columns on the same side
const DIM_COLUMN_SPACING = 12;
// how far the extension line overshoots past the dimension line
const DIM_EXT_OVERSHOOT = 3;
// length of the tick mark at each end of the dimension line
const DIM_TICK = 3;
// padding on each side of the text within the break in the dimension line
const DIM_TEXT_PAD = 3;

export const TEXT_BASELINE_ADJUST = 2;

// MSDF bitmap font registered by DrawFrmApp.waitFonts(). color comes from the
// label's tint (set on creation), not the style.
const TEXT_STYLE: Partial<IBitmapTextStyle> = {
    fontName: "Arial",
    fontSize: 10,
};

export type DimensionKind = "standard" | "extended";

export class FrmShapeDimension {
    shape: FrmShape;
    pointA: FrmShapePoint;
    pointB: FrmShapePoint;

    // explicit side override. when null, the side is derived from orientation
    // (vertical -> right, horizontal -> bottom).
    sideOverride: DimensionSide | null;

    expectedLength: number = 0;

    frmVar: FrmVar;
    enableVarReporting: boolean = false;

    label: BitmapText | null = null;
    kind: DimensionKind;

    // when true this dimension's length constraint still applies (done() ignores
    // this), but it isn't rendered — used by clamshell mullions to keep a pen-up
    // half's face/depth sized without drawing its now-redundant dimensions.
    hidden: boolean = false;

    // subscription on frmVar; cancelled in destroy() so a disposed dimension
    // (and the shape graph behind it) isn't pinned alive by the long-lived
    // FrmVar's listener list.
    private frmVarSub: Subscription;

    constructor(shape: FrmShape, pointA: FrmShapePoint, pointB: FrmShapePoint, frmVar: FrmVar, kind: DimensionKind, side?: DimensionSide) {
        this.shape = shape;
        this.pointA = pointA;
        this.pointB = pointB;
        this.frmVar = frmVar;
        this.kind = kind;
        this.sideOverride = side ?? null;

        this.expectedLength = this.frmVar.get();
        this.frmVarSub = this.frmVar.subscribe((ev) => {
            this._changeValue(this.frmVar.get(), ev);
        })
    }

    destroy() {
        this.frmVarSub.cancel();
    }

    // hide this dimension's label. the dimension line itself lives in the
    // shape's shared graphics, which is cleared each draw(), so skipping the
    // draw already removes the line; only the persistent label needs hiding.
    hide() {
        if (this.label) this.label.visible = false;
    }

    get length(): number {
        return distance(this.pointA, this.pointB);
    }

    // a dimension is vertical when it measures a (mostly) vertical span between its points.
    get isVertical(): boolean {
        return Math.abs(this.pointB.y - this.pointA.y) >= Math.abs(this.pointB.x - this.pointA.x);
    }

    get side(): DimensionSide {
        if (this.sideOverride) return this.sideOverride;
        return this.isVertical ? "right" : "bottom";
    }

    minRange = 50;

    // span of the dimension along the axis it measures (Y for vertical, X for horizontal).
    // used to detect overlap when stacking dimensions on the same side.
    get spanMin(): number {

        const a = this.isVertical ? this.pointA.y : this.pointA.x;
        const b = this.isVertical ? this.pointB.y : this.pointB.x;
        const range = Math.abs(a-b);
        const min = Math.min(a, b)

        if(range < this.minRange) {
            const adjustBy = (this.minRange - range) / 2;
            return min - adjustBy;
        }

        return min;
    }

    get spanMax(): number {

        const a = this.isVertical ? this.pointA.y : this.pointA.x;
        const b = this.isVertical ? this.pointB.y : this.pointB.x;
        const range = Math.abs(a-b);
        const max = Math.max(a, b)

        if(range < this.minRange) {
            const adjustBy = (this.minRange - range) / 2;
            return max + adjustBy;
        }

        return max;
    }

    // user-defined dimensions are ones the user has explicitly edited. they
    // render blue and are treated as fixed: other dimension edits won't change
    // them (the only exception is editing this dimension itself). default
    // dimensions are free to be adjusted by the shape engine.
    get userDefined(): boolean {
        return this.frmVar.isModified();
    }

    private ensureLabel(): BitmapText {
        if (this.label) return this.label;

        const t = new BitmapText("", TEXT_STYLE);
        t.anchor.set(0.5, 0.5);
        t.tint = 0x000000;

        t.eventMode = "static";
        t.cursor = "pointer";
        t.on("pointertap", (e) => {
            e.stopPropagation();

            this.shape.app.showReact({
                render: (opts) => this.shape.app.renderDimension({
                    value: this.length,
                    locked: this.userDefined,
                    onLockChange: (locked: boolean) => {
                        this.frmVar.userClearModified();
                        opts.remove();
                        this.shape.refresh();
                    },
                    onDone: (value: number) => {
                        opts.remove();
                        const beforeValue = this.frmVar.get();
                        const beforeModified = this.frmVar.isModified();

                        try {
                            this.changeValue(value, "usr-dimension-edit");
                        } catch (e: any) {
                            console.warn(e);

                            this.frmVar.restore(beforeValue, beforeModified);
                            this.shape.app.showError(e.toString());
                        }
                    },
                    onCancel: () => {
                        opts.remove();
                    }
                }),
                anchor: () => t.getGlobalPosition()
            });
        });


        this.shape.thing.graphic.addChild(t);
        this.label = t;

        return t;
    }

    // render this dimension on the outside of the drawing's bounding box.
    // `column` is the stacking column assigned by the shape (0 = closest to the
    // geometry); overlapping dimensions get a higher column and sit further out.
    // renders as an engineering dimension: extension lines from the measured
    // points, end ticks, and a line broken in the middle for the value text.
    draw(g: Graphics, column: number, bbox: BBox, shapeUpdateId?: number) {
        const a = this.pointA;
        const b = this.pointB;

        // user-defined dimensions render in the user color, others use the
        // default styling. style is overridable per app instance.
        const style = this.shape.app.dimensionStyle;
        const color = this.userDefined ? style.userColor : style.defaultLineColor;
        this.shape.app.setDimensionLineStyle(g, color);

        // position the label first so we can size the break to fit it
        const label = this.ensureLabel();
        label.visible = true;

        if(this.enableVarReporting) {
            if(Math.abs(this.length - this.frmVar.get()) > EPS) {
                console.warn(this.shape.name, "mismatch length and var", this.length, this.frmVar.get())
                this.frmVar.set(this.length, {
                    debug: "draw-mismatch",
                    shapeUpdateId: shapeUpdateId || FrmShape.getUpdateLengthId()
                });
            }
        }

        label.text = this.shape.app.formatDimension(this.length);
        label.tint = this.userDefined ? style.userColor : style.defaultLabelColor;

        // hit-test against the text's bounding box rather than the glyph pixels,
        // so taps in the whitespace between letters still register. anchor is
        // (0.5, 0.5), so local bounds run from -w/2..w/2, -h/2..h/2. the rectangle
        // is in unrotated local space; the label's rotation transform handles the
        // rest, so the same box works for vertical and horizontal dimensions.
        label.hitArea = new Rectangle(-label.width * 0.5, -label.height * 0.5, label.width, label.height);

        if (this.isVertical) {
            // outward sign along X (right = +, left = -)
            const sign = this.side === "right" ? 1 : -1;
            const edge = this.side === "right" ? bbox.maxX : bbox.minX;
            const x = edge + sign * (DIM_BASE_GAP + column * DIM_COLUMN_SPACING);

            const yMin = Math.min(a.y, b.y);
            const yMax = Math.max(a.y, b.y);
            const yMid = (yMin + yMax) * 0.5;

            // extension (witness) lines straddling the dimension line at each measured point
            drawExtensionLine(g, a.y, x, sign, true);
            drawExtensionLine(g, b.y, x, sign, true);

            // end ticks
            g.moveTo(x - DIM_TICK, yMin); g.lineTo(x + DIM_TICK, yMin);
            g.moveTo(x - DIM_TICK, yMax); g.lineTo(x + DIM_TICK, yMax);

            // vertical text reads up the line
            label.rotation = -Math.PI / 2;
            label.position.set(x + TEXT_BASELINE_ADJUST, yMid);

            // dimension line, broken in the middle to make room for the text
            const half = label.width * 0.5 + DIM_TEXT_PAD; // text is rotated, so width runs along Y
            if (yMax - yMin > half * 2) {
                g.moveTo(x, yMin); g.lineTo(x, yMid - half);
                g.moveTo(x, yMid + half); g.lineTo(x, yMax);
            } else {
                // no line if text would be too long
            }
        } else {
            // outward sign along Y (bottom = +, top = -)
            const sign = this.side === "bottom" ? 1 : -1;
            const edge = this.side === "bottom" ? bbox.maxY : bbox.minY;
            const y = edge + sign * (DIM_BASE_GAP + column * DIM_COLUMN_SPACING);

            const xMin = Math.min(a.x, b.x);
            const xMax = Math.max(a.x, b.x);
            const xMid = (xMin + xMax) * 0.5;

            drawExtensionLine(g, a.x, y, sign, false);
            drawExtensionLine(g, b.x, y, sign, false);

            g.moveTo(xMin, y - DIM_TICK); g.lineTo(xMin, y + DIM_TICK);
            g.moveTo(xMax, y - DIM_TICK); g.lineTo(xMax, y + DIM_TICK);

            label.rotation = 0;
            label.position.set(xMid, y+TEXT_BASELINE_ADJUST);

            const half = label.width * 0.5 + DIM_TEXT_PAD;
            if (xMax - xMin > half * 2) {
                g.moveTo(xMin, y); g.lineTo(xMid - half, y);
                g.moveTo(xMid + half, y); g.lineTo(xMax, y);
            } else {
                g.moveTo(xMin, y); g.lineTo(xMax, y);
            }
        }
    }

    // updateLength adjusts points to ensure the distance between pointA and pointB is equal to value.
    // related points are adjusted to maintain shape integrity and rules (flexLength, fixedLength).
    changeValue(value: number, strDebug?: string): void {
        this.frmVar.userSet(value, {
            debug: strDebug || "changeValue",
            shapeUpdateId: FrmShape.getUpdateLengthId()
        });
    }

    private _changeValue(value: number, ev: FrmVarEvent) {
        this.expectedLength = value;
        this.shape.updateLength(ev.shapeUpdateId || FrmShape.getUpdateLengthId(), this.pointA, this.pointB, value, ev.debug, this);
    }

    isParallel(uvX: number, uvY: number): boolean {
        const dx = this.pointB.x - this.pointA.x;
        const dy = this.pointB.y - this.pointA.y;

        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < EPS) return false;

        // cross product of unit vectors; zero => parallel (same or opposite direction)
        return Math.abs((dx / len) * uvY - (dy / len) * uvX) < EPS;
    }

    // assumes the dimension direction (pointA -> pointB) is parallel to (uvX, uvY)
    // as tested by isParallel. returns true when it points the opposite way.
    isReverseDirection(uvX: number, uvY: number) {
        const dx = this.pointB.x - this.pointA.x;
        const dy = this.pointB.y - this.pointA.y;

        // dot product with the reference direction; negative => opposite direction
        return dx * uvX + dy * uvY < 0;
    }
}

// distance the extension (witness) line reaches inward toward the drawing from
// the dimension line. it's a short stub, it does not run all the way to the point.
const DIM_EXT_INWARD = DIM_COLUMN_SPACING / 4;

// draws a short witness line straddling the dimension line at the measured point's
// position along it. `coord` is the point's position along the dimension line
// (its y for vertical dimensions, its x for horizontal). `target` is the
// dimension line's x (vertical) or y (horizontal).
function drawExtensionLine(g: Graphics, coord: number, target: number, sign: number, vertical: boolean) {
    const outer = target + sign * DIM_EXT_OVERSHOOT;   // slightly past the dimension line
    const inner = target - sign * DIM_EXT_INWARD;      // short stub toward the drawing
    if (vertical) {
        g.moveTo(inner, coord); g.lineTo(outer, coord);
    } else {
        g.moveTo(coord, inner); g.lineTo(coord, outer);
    }
}


// greedy interval-graph coloring: assigns each dimension to the lowest-indexed
// column whose existing dimensions don't overlap its span. non-overlapping
// dimensions share a column; overlapping ones get bumped to the next column out.
export function assignDimColumns(dims: FrmShapeDimension[]): number[] {
    const order = dims.map((_, i) => i).sort((i, j) => dims[i].spanMin - dims[j].spanMin);

    const result = new Array<number>(dims.length);
    const columnMax: number[] = []; // current spanMax occupying each column

    for (const i of order) {
        const dim = dims[i];
        let placed = -1;
        for (let c = 0; c < columnMax.length; c++) {
            if (columnMax[c] <= dim.spanMin + EPS) {
                placed = c;
                break;
            }
        }
        if (placed === -1) {
            placed = columnMax.length;
            columnMax.push(dim.spanMax);
        } else {
            columnMax[placed] = dim.spanMax;
        }
        result[i] = placed;
    }

    return result;
}
